<template>
  <div :class="!isEmbedded && !isUi ? 'p-1 p-sm-4' : ''">
    <div class="mb-4" v-if="isUi">
      <h4 class="font-weight-bold mb-2">
        <template v-if="uri && scores && scores.details">
          <span class="text-muted font-weight-light">Insights <span class="material-icons md-14 arrow-east">east</span> <span class="text-muted cursor-pointer" @click="restart">Score</span> <span class="material-icons md-14 arrow-east">east</span></span> {{ uriWithoutPrefix }}
        </template>
        <template v-else>
          <span class="text-muted font-weight-light">Insights</span> <span class="material-icons md-14 arrow-east">east</span> Score
        </template>
      </h4>
    </div>
    <div class="my-5 text-center" v-if="!isEmbedded && !isUi">
      <a href="https://exatom.io/?utm_source=form-audit-website" target="_blank">
        <img src="@/assets/exatom-logo_tagline.svg" width="240" alt="Exatom"/>
      </a>
    </div>
    <div :class="!isEmbedded ? 'card card-body' : 'mb-4'">
      <div
        v-if="isShareUri && ! errorLoadingResult && (!scores || !scores.details || !Object.values(scores.details).length)"
        class="text-center animate__animated animate__fadeIn">
        <img width="100" height="100" alt="Magic is happening, stay tuned!" src="@/assets/form-loader-transparent.gif"/>
      </div>
      <div v-if="isShareUri && errorLoadingResult"
           class="text-center animate__animated animate__fadeIn">
        <p>Score results are only available for 90 days. Click <a href="/">here</a> to run a new score analysis.</p>
        <p>If you believe this is an error, you can <a
          :href="`mailto:support@exatom.io?subject=Exatom%20Score%20-%20unable%20to%20find%20form%20score%20results&body=Hi!%0D%0A%0D%0AI%20was%20not%20able%20to%20retrieve%20my%20score%20results%2C%20could%20you%20have%20a%20look%3F%0D%0A${uri}`">reach
          out</a>.</p>
      </div>
      <div v-if="!isShareUri && (!scores || !scores.details || !Object.values(scores.details).length)"
           class="text-center m-3 m-sm-5"
           :class="isEmbedded ? 'animate__animated animate__fadeIn' : ''">
        <div class="mb-2">
          <div class="mb-4">
            <img width="150" height="150" alt="Exatom Score" src="@/assets/user-laptop-iphone.svg" />
          </div>
          <h4 class="display-5 text-primary font-weight-bolder text-uppercase line-after-centered">Score your form</h4>
          <div class="text-primary font-weight-bold" style="font-size: 115%;">
            <p v-if="!isUi">At Exatom, we're dedicated to creating privacy-first tools that help you build better forms.</p>
            <p v-else>We're dedicated to creating privacy-first tools that help you build better forms.</p>
          </div>
          <div :class="immediateAnalysis && isEmbedded ? 'd-none' : ''">
            <div style="max-width: 54.25rem; margin-left: auto; margin-right: auto;">Our scoring tool provides a thorough analysis of your form with recommendations for improvement. You'll get an overall score and detailed feedback about accessibility, user experience, performance, code quality and best practices. We'll show you how to fix errors and provide insights into the issues that are hurting your conversion rates.</div>
            <form name="score" id="score" @submit.prevent="scoreUri" class="d-flex flex-wrap justify-content-center mt-3 mt-sm-4">
              <b-input name="uriInput" id="uriInput" type="text" placeholder="https://yourwebsite.io/form" autocapitalize="none" autofocus trim
                       ref="uriInput" v-model="uri"
                       class="mb-2 mb-sm-1 mr-1" style="height: 2.4rem; max-width: 24rem;"/>
              <button name="scoreFormsBtn" id="scoreFormsBtn" type="submit" :disabled="loading"
                      class="btn btn-success" style="width: 9rem; padding: 0; height: 2.4rem;">
                Score form <i class="material-icons material-icons-outlined"
                              style="font-size: 1rem; vertical-align: text-top;">track_changes</i>
              </button>
            </form>
          </div>
        </div>
        <div v-if="loading" class="mt-1">
          <img width="100" height="100" alt="Magic is happening, stay tuned!"
               src="@/assets/form-loader-transparent.gif"/>
        </div>
        <div v-if="disallowedDomain === true" class="mt-3">
          <b-alert variant="primary" show class="mt-3">
            <div class="mb-2">
              Only forms on <span class="text-primary font-weight-bold">{{ $root.getDomains().join(', ') }}</span> are allowed to be scored.
            </div>
            <div class="mb-2">
              Add a new website to your account to score <span class="text-primary font-weight-bold">{{ $root.extractHostname(uri) }}</span>
            </div>
            <router-link :to="{ name: 'websites-add', params: { website: encodeURIComponent($root.extractHostname(uri)) } }" class="btn btn-create btn-sm">Create website: {{ $root.extractHostname(uri) }}</router-link>
          </b-alert>
        </div>
        <div v-if="couldNotScore === true" class="text-danger animate__animated animate__bounce mt-3">
          <p>Oops, we're unable to score your form.</p>
          <p>Try our <a href="https://chrome.google.com/webstore/detail/exatom-webform-audit/gafmmfoddcaabpmapakfafphlikdkpoj">Chrome extension</a>, it's compatible with more complex forms.</p>
          Still a problem? <a :href="`mailto:support@exatom.io?subject=Exatom%20Score%20-%20unable%20to%20score%20form&body=Hi!%0D%0A%0D%0AI%20was%20not%20able%20to%20score%20my%20form%2C%20could%20you%20have%20a%20look%3F%0D%0A${uri}`">Let us know</a> and we'll get in touch!
        </div>
      </div>
      <div v-if="scores && scores.details && Object.values(scores.details).length"
           :class="isEmbedded ? 'animate__animated animate__zoomIn' : 'animate__animated animate__fadeIn'">
        <!-- Header -->
        <div v-if="!isEmbedded"
             class="text-center text-sm-left d-sm-flex justify-content-sm-start">
          <div class="w-100">
            <h3 class="text-primary mb-1">Score</h3>
            <div class="display-6 text-nowrap text-ellipsis" style="max-width: 38rem;">
              <span class="material-icons material-icons-outlined d-none d-sm-inline" style="font-size: 1.25rem; vertical-align: middle;">description</span> <a :href="uri" target="_blank" class="text-secondary">{{ uriWithoutPrefix }}</a>
            </div>
          </div>
          <div style="min-width: 15rem;" class="text-center text-sm-right d-print-none mt-4 mt-sm-0">
            <b-btn variant="success" @click="restart">
              <span class="material-icons" style="font-size: 1rem; vertical-align: text-top;">restart_alt</span>
              New score analysis
            </b-btn>
          </div>
          <div style="min-width: 20rem;" class="text-right display-6 d-none d-print-block">
            Find out how your forms score at <span class="text-primary">exatom.io</span>
          </div>
        </div>

        <!-- Summary -->
        <div class="d-flex justify-content-center">
          <div class="w-75 row mt-3">
            <div class="col-sm-5 col d-flex align-items-center justify-content-center justify-content-sm-end m-0 mr-sm-3">
              <qrcode-vue v-if="resultHash" :value="shareUri" size="160" level="H"
                          class="d-none d-print-block mr-4"></qrcode-vue>
              <div class="text-center mb-4 mb-sm-0">
                <b-avatar size="10rem" :text="Number.parseFloat(scores.score).toString()" square variant="primary"
                          class="text-white font-weight-bolder letter-spacing-1px d-block"/>

                <!-- Sharing functions -->
                <div class="d-print-none d-flex justify-content-between m-2 mt-3">
                  <ShareNetwork
                    network="linkedin"
                    url="https://score.exatom.io"
                    title="I just scored my form with Exatom 🚀 interesting results!"
                    description="Gain insights on how your form is build and get to know how you can optimize without tagging your website."
                    hashtags="exatom,FormAnalytics,CRO"
                  >
                    <font-awesome-icon icon="fa-brands fa-linkedin-in" class="text-light display-6 mr-1"/>
                  </ShareNetwork>
                  <ShareNetwork
                    network="twitter"
                    url="https://score.exatom.io"
                    title="I just scored my form with Exatom 🚀 interesting results!"
                    description="Gain insights on how your form is build and get to know how you can optimize without tagging your website."
                    hashtags="exatom,FormAnalytics,CRO"
                  >
                    <font-awesome-icon icon="fa-brands fa-twitter" class="text-light display-6"/>
                  </ShareNetwork>
                  <ShareNetwork
                    network="email"
                    :url="shareUri"
                    title="Check out our form score report by Exatom"
                  >
                    <span class="material-icons text-light display-6"
                          style="vertical-align: text-bottom;">email</span>
                  </ShareNetwork>
                  <a :href="shareUri"
                     target="_blank"
                     v-clipboard:copy="shareUri"
                     v-clipboard:success="() => $refs.linkCopy.classList.add('text-success', 'animate__heartBeat')"
                     ref="linkCopy"
                     class="material-icons text-light display-6 cursor-pointer animate__animated no-underline"
                     style="vertical-align: text-bottom;">link</a>
                </div>
              </div>
            </div>
            <div class="col">
              <div v-for="(scoreGroup, name) in scores.details" :key="name" class="my-1">
                <template v-if="scoreGroup.weight">
                  <div class="font-weight-bolder text-primary text-uppercase small">{{ name }} <span
                    class="material-icons material-icons-outlined"
                    style="font-size: 0.7rem; vertical-align: middle;">east</span> {{ scoreGroup.score }} / 10
                  </div>
                  <b-progress :id="'progress' + name.replaceAll(' ', '')" :value="scoreGroup.score" height="10px" max="10"
                              :variant="'score' + name.replaceAll(' ', '')" class="m-0 mb-3"></b-progress>
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- Sign-up CTA -->
        <div
          v-if="(!$root.client && !Object.values(scores.details).filter((sg) => sg.checks && sg.checks.length).filter(Boolean).length)"
          class="m-3 m-sm-4 d-print-none card card-body"
        >
          <div class="row m-0">
            <div class="col col-sm-8 p-3 p-sm-4">
              <template v-if="!formAuditFullReport.reportSuccess">
                <h3 class="text-primary font-weight-bolder">Receive the full report by email for free</h3>
                <div class="mb-3">
                  Fill in your details and we’ll send you the full report of your form audit, with all 25 checks on the 7 dimensions. For free!
                </div>
                <form name="formAuditFullReport" id="formAuditFullReport">
                  <label for="your-name" class="text-uppercase font-weight-bolder m-0">Name *</label>
                  <div class="mb-3">
                    <b-input type="text" autocomplete="name" ref="yourName" name="your-name" id="your-name" minlength="3" required v-model="formAuditFullReport.name" />
                    <div v-if="formAuditFullReport.nameError" class="small text-danger">Please enter your name</div>
                  </div>
                  <label for="your-email" class="text-uppercase font-weight-bolder m-0">Work email *</label>
                  <div class="mb-3">
                    <b-input type="email" autocomplete="email" ref="yourEmail" name="your-email" id="your-email" required v-model="formAuditFullReport.email" />
                    <div v-if="formAuditFullReport.emailError" class="small text-danger">Please enter your work email address</div>
                  </div>
                  <div class="text-center text-sm-left">
                    <button type="button" class="btn btn-success" @click="sendAuditReport">Email my report <span class="material-icons md-14 arrow-east" style="vertical-align: text-top;">east</span></button>
                  </div>
                </form>
                <div class="text-secondary small mt-3" style="opacity: 0.75;">
                  By clicking ‘Send message’ you explicitly agree to the processing of your personal data as indicated in our <a href="https://exatom.io/privacy-policy/" target="_blank">Privacy Policy</a>.
                </div>
                <div class="text-danger small mt-3" v-if="formAuditFullReport.reportSuccess === false">
                  Something went wrong, please send us an email at <a :href="'mailto:support@exatom.io?subject=Full%20form%20audit%20report%20error&body=' + encodeURIComponent(shareUri)">support@exatom.io</a>
                </div>
              </template>
              <template v-else>
                <h3 class="text-primary font-weight-bolder">The form audit report is in your inbox!</h3>
                Companies worldwide, from startups to the biggest corporations use Exatom to optimise their forms. Start a <a href="https://olympus.exatom.io/signup" target="_blank">15-day free trial</a> to discover your form potential or book a <a :href="this.meetingUrl" target="_blank">meeting</a> with one of our experts.
                <div class="mt-3 text-center">
                  <img class="mb-3" loading="lazy" decoding="async" width="75" src="https://exatom.io/wp-content/uploads/2024/03/logo-hema_black.svg" title="Hema" alt="Hema" />
                  <img class="mb-3" loading="lazy" decoding="async" width="75" src="https://exatom.io/wp-content/uploads/2024/03/logo-veritas_black.svg" title="Veritas" alt="Veritas" />
                  <img class="mb-3" loading="lazy" decoding="async" width="75" src="https://exatom.io/wp-content/uploads/2024/03/logo-deltafiber_black.svg" title="Delta" alt="Delta" />
                  <img class="mb-3" loading="lazy" decoding="async" width="75" src="https://exatom.io/wp-content/uploads/2024/03/logo-vgz_black.svg" title="VGZ" alt="VGZ" />
                  <img class="mb-3" loading="lazy" decoding="async" width="75" src="https://exatom.io/wp-content/uploads/2024/03/logo-bmw_black.svg" title="BMW" alt="BMW" />
                  <img class="mb-3" loading="lazy" decoding="async" width="75" src="https://exatom.io/wp-content/uploads/2024/03/logo-brooks_black.svg" title="Brooks Running" alt="Brooks Running" />
                  <img class="mb-3" loading="lazy" decoding="async" width="75" src="https://exatom.io/wp-content/uploads/2024/03/logo-inno_black.svg" title="Inno" alt="Inno" />
                  <img class="mb-3" loading="lazy" decoding="async" width="75" src="https://exatom.io/wp-content/uploads/2024/03/logo-kia_black.svg" title="Kia" alt="Kia" />
                  <img class="mb-3" loading="lazy" decoding="async" width="75" src="https://exatom.io/wp-content/uploads/2024/03/logo-toyota_black.svg" title="Toyota" alt="Toyota" />
                </div>
              </template>
            </div>
            <div class="col-4 d-none d-sm-block pt-5">
              <img src="https://exatom.io/wp-content/uploads/2022/06/visual_score-app.svg" title="Form audit">
            </div>
          </div>
        </div>

        <!-- Score breakouts -->
        <div v-else :class="isEmbedded ? 'mx-4' : ''">
          <div class="card mt-4 pb-3"
               v-for="(scoreGroup, name) in scores.details" :key="name"
               :style="'border-left: 4px solid var(--color-score' + name.replaceAll(' ', '') + ') !important;'">
            <div class="card-body">
              <div>
                <h3 class="text-primary">{{ name }}</h3>
                <div v-if="scoreGroup.description">
                  <div class="scoreGroupDescriptionTextOutline"></div>
                  {{ scoreGroup.description }}
                  <a v-if="scoreGroup.link" :href="scoreGroup.link" target="_blank"><i class="material-icons info"
                                                                                       style="vertical-align: bottom;">info</i></a>
                </div>
              </div>
              <div style="position: absolute; top: 1.3rem; right: 1.3rem;">
                <b-avatar v-if="scoreGroup.score" size="4rem" :text="scoreGroup.score.toString()" :class="'bg-score' + name.replaceAll(' ', '')"
                          class="text-white font-weight-bolder breakout-group-score" />
              </div>
            </div>
            <div class="card card-body mb-2 p-3 mx-3" v-for="check in scoreGroup.checks" :key="check.name">
              <div class="d-flex justify-content-between">
                <div v-b-toggle="check.id" class="w-100 ml-3">
                  <span class="material-icons material-icons-outlined cursor-pointer"
                        style="font-size: 1rem; vertical-align: text-top; color: var(--exatom-grey); position: absolute; left: 0.5rem; top: 1.15rem;">
                      unfold_more
                  </span>
                  <span class="font-weight-bold">{{ check.name }}</span>
                </div>
                <div style="width: 5rem;" class="text-right text-nowrap ml-1">
                  <div v-if="check.score" v-b-tooltip.left :title="check.score + ' out of 10'">
                    <span v-for="value in (new Array(Math.floor(Number.parseFloat(check.score)/2)))"
                          :key="value + '-' + Math.random()" class="material-icons material-icons-outlined"
                          style="font-size: 1rem; vertical-align: text-top;">star</span><span
                    v-if="(Number.parseFloat(check.score)/2)-Math.floor(Number.parseFloat(check.score)/2)"
                    class="material-icons material-icons-outlined" style="font-size: 1rem; vertical-align: text-top;">star_half</span>
                  </div>
                  <div v-else-if="check.score === 0 && check.weight !== null && check.weight >= 0" v-b-tooltip.left title="0 out of 10"><span
                    class="material-icons material-icons-outlined text-danger"
                    style="font-size: 1rem; vertical-align: text-top;">warning</span></div>
                  <div v-else class="small" style="margin-top: 0.1rem;" v-b-toggle="check.id">Learn more</div>
                </div>
              </div>
              <b-collapse :id="check.id" :ref="check.id">
                <div class="mt-3 mx-3 px-3 py-2 font-weight-light"
                     :style="'border-left: 2px solid var(--color-score' + name.replaceAll(' ', '') + ') !important;'"
                     v-html="check.description || 'No additional information available'"
                >
                </div>
              </b-collapse>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="scores && scores.form && scores.details && Object.values(scores.details).filter((sg) => sg.checks && sg.checks.length).filter(Boolean).length" class="card card-body d-block mt-3" :class="isEmbedded ? 'mx-4 mb-4' : ''">
      <h4 class="text-primary font-weight-bold">Form details</h4>
      <p><b>Fields ({{ scores.form.elements.length }}):</b> {{ scores.form.elements.join(', ') }}</p>
      <p><b>Name or identifier:</b> {{ scores.form.name ? scores.form.name : 'not set' }}</p>
      <p><a href="#" @click="toggleShowImprovementAreasOnly">Show improvements only {{ showImprovementAreasOnly ? '(ON)' : '(OFF)' }}</a></p>
      <a href="#" @click="downloadAsCsv">Download as CSV</a>
    </div>
    <footer v-if="!isEmbedded && !isUi" class="small text-center font-weight-light my-5">
      <a href="https://exatom.io/?utm_source=form-audit-chrome" target="_blank">
        <div>
          A form optimization tool brought you by
        </div>
        <div>
          <img src="@/assets/exatom-logo_grey.svg" width="86" height="25" alt="Exatom">
        </div>
      </a>
      <div class="social">
        <a href="https://www.linkedin.com/company/exatom/" target="_blank" class="mr-1"><font-awesome-icon icon="fa-brands fa-linkedin-in" /></a>
        <a href="https://twitter.com/Exatom_io/" target="_blank"><font-awesome-icon icon="fa-brands fa-twitter" /></a>
      </div>
    </footer>
  </div>
</template>

<script>
import QrcodeVue from 'qrcode.vue'
import axios from 'axios'

export default {
  metaInfo: {
    title: 'Score'
  },
  name: 'ScoreComponent',
  components: { QrcodeVue },
  data () {
    return {
      loading: false,
      uri: '',
      scoreData: {},
      couldNotScore: false,
      errorLoadingResult: false,
      disallowedDomain: false,
      showImprovementAreasOnly: false,
      formAuditFullReport: {
        name: null,
        email: null,
        nameError: false,
        emailError: false,
        reportSuccess: null // null|true|false => not sent|success|failure
      }
    }
  },
  computed: {
    meetingUrl () {
      const lang = document.documentElement.getAttribute('lang')
      switch (lang) {
        case 'nl-NL':
        case 'nl':
          return 'https://calendly.com/exatom/exatom-nl-30mins'

        case 'de-DE':
        case 'de':
          return 'https://calendly.com/exatom/exatom-termin-de-30min'

        case 'fr-FR':
        case 'fr':
          return 'https://calendly.com/matthieu_exatom/45-min-fr'

        case 'en-EN':
        default:
          return 'https://calendly.com/exatom/30min'
      }
    },
    fullReportLink () {
      if (document.referrer?.indexOf('/nl/') !== -1) {
        return 'https://exatom.io/nl/formulier-audit-rapport-per-email/?uri=' + encodeURI(document.location.href)
      } else if (document.referrer?.indexOf('/de/') !== -1) {
        return 'https://exatom.io/de/formular-audit-report-per-e-mail/?uri=' + encodeURI(document.location.href)
      } else if (document.referrer?.indexOf('/fr/') !== -1) {
        return 'https://exatom.io/fr/rapport-daudit-de-formulaire-par-e-mail/?uri=' + encodeURI(document.location.href)
      }
      return 'https://exatom.io/form-audit-report-by-email/?uri=' + encodeURI(document.location.href)
    },
    scores: {
      get () {
        if (!this.scoreData?.details) return {}
        if (this.showImprovementAreasOnly) {
          const improvements = structuredClone(this.scoreData)

          for (const groupName of Object.keys(this.scoreData?.details)) {
            improvements.details[groupName].checks = this.scoreData.details[groupName].checks.filter(c => c.weight !== null && c.score !== null && c.score < 8)
            if (!improvements.details[groupName].checks.length) {
              delete improvements.details[groupName]
            }
          }
          return improvements
        }
        return this.scoreData
      },
      set (value) {
        this.scoreData = value
      }
    },
    uriWithoutPrefix () {
      return this.uri?.trim().replaceAll(/^https?:\/\/(www\.)?/gi, '')
    },
    isEmbedded () {
      return Object.keys(this.$route.query).includes('embed')
    },
    isUi () {
      return typeof this.$root.client !== 'undefined'
    },
    resultHash () {
      if (!this.isShareUri && !this.scores?.json) return
      return this.$route.params?.hash ?? this.scores.json.split('/')?.pop()?.replace('.json', '')
    },
    shareUri () {
      const baseUri = new URL(this.resultHash ? (!this.isUi ? document.location.origin : 'https://score.exatom.io') + '/result/' + this.resultHash : document.location.origin)
      if (this.showImprovementAreasOnly) {
        baseUri.searchParams.append('showImprovementAreasOnly', 1)
      }
      return baseUri.toString()
    },
    isShareUri () {
      return typeof this.$route.params?.hash !== 'undefined'
    },
    immediateAnalysis () {
      return !!this.$route.params.url
    }
  },
  async mounted () {
    if (this.isEmbedded && Object.keys(this.$route.query).includes('bg') && (this.$route.query.bg === 'transparent' || this.$route.query.bg.startsWith('#'))) {
      document.body.style.background = this.$route.query.bg
      document.getElementById('scoreApp').style.background = this.$route.query.bg
    }
    await this.reload()

    // Instructed to do immediately an analysis?
    if (this.immediateAnalysis) {
      this.uri = this.$route.params.url
      this.scoreUri()
    }
  },
  methods: {
    async reload () {
      if (this.loading) return
      this.$root.$api.addTags()

      this.loading = true

      if (this.$route.params?.hash || this.$route.query?.token) {
        const jsonUri = `https://exassets.s3.eu-central-1.amazonaws.com/scores/${this.$route.query?.token ?? this.$route.params.hash}.json`
        await axios.get(jsonUri).then((response) => {
          if (response.data) {
            this.scores = response.data
            this.scores.json = jsonUri
            this.uri = this.scores.uri
          }
        }).catch(() => {
          this.errorLoadingResult = true
          this.uri = document.location.href
        })
      } else {
        this.scores = {}
        // eslint-disable-next-line
        // this.scores = {"score":7,"form":{"name":"dwfrm_profile","elements":["Dhr.","Mevr.","Voornaam","Naam","dwfrm_profile_customer_phoneprefix","Telefoonnummer","Geboortedatum","Email adres","Wachtwoord","Registreren","Ik ben al lid","Liever niet","Loyalty Card nummer","Ik heb de Algemene Voorwaarden en Privacy verklaring gelezen en ga hiermee akkoord.","Geniet van de nieuwste aanbiedingen en blijf op de hoogte door je aan te melden voor de INNO nieuwsbrief waarin we je inspiratie geven voor je nieuwe outfits."],"location":"https:\/\/www.inno.be\/nl\/my-account\/create"},"details":{"E-Commerce":{"weight":null,"score":null,"link":null,"description":"Online forms play an important role in the checkout process on webshops. Let's dive deeper and focus on the interaction with the visitors of the webshops, and we look at how we can improve the user experience during the checkout process.","checks":[{"name":"Guest checkout","description":"We experienced that over 65% of all users prefer not to create an account during checkout. Forcing your visitors to leave additional (sensitive) information and making them invest even more time into the completion of their order will result in a smaller chance of them converting. Giving your visitors the option to continue as a guest will take those conversion killers away and increase the user experience on your checkout process.","score":null,"weight":null,"id":"80f3feb6-c492-4677-909b-930479dbdb3a"},{"name":"Payment options","description":"Make sure you provide enough payment options for your visitors. The desired payment options differ per region. If you have a webshop and you sell to people in the Netherlands, it is important to include services like iDEAL and Klarna Afterpay. But if you intend to include Belgium and Germany, it is important to also include PayPal, Bancontact, Sofort banking and Giropay. Every visitor that is unable to finish their order because they are not able to pay in their preferred payment method is a big waste for your conversion.","score":null,"weight":null,"id":"c20c4688-dcaa-4cdc-a826-b64e2de7223b"}]},"Accessibility":{"weight":6,"score":9,"description":"Accessibility is the inclusive practice of ensuring there are no barriers that prevent interaction with, or access to, websites on the internet by people with physical disabilities, situational disabilities, and socio-economic restrictions on bandwidth and speed. When websites and forms are correctly designed, developed and edited, more users have equal access to information and functionality.","link":"https:\/\/en.wikipedia.org\/wiki\/Web_accessibility","checks":[{"name":"Ability to submit your form","score":10,"weight":6,"description":"Sounds obvious but a button to submit the form should be available. This is preferred to be in the form of an <code class=\"inline\">&lt;input type=&quot;submit&quot; \/&gt;<\/code> or <code class=\"inline\">&lt;button type=&quot;submit&quot; \/&gt;<\/code> as those provide additional accessibility contexts by default in all modern browsers. For example a link (<code class=\"inline\">&lt;a&gt;<\/code>) or containers (<code class=\"inline\">&lt;span&gt;<\/code>, <code class=\"inline\">&lt;div&gt;<\/code>) are not accessible by default.","id":"d36cb1c8-b518-46a3-ab1f-0cbf02cf6668"},{"name":"Contrasting field borders","score":7,"weight":8,"description":"When a visitor glances at your form, it should be clear where fields are located and what needs completion. One of the techniques for this is to set a contrasting field border.","id":"51131a37-2e0f-4f26-a01f-26304f70419f"},{"name":"Contrasting field input text color","score":10,"weight":6,"description":"After a visitor starts to complete your form, the entered data should be clear to read. Additionally, good contrast between your field background and text makes it easy for the visitor to make corrections where needed.","id":"62a3eb87-6a0b-482b-aa27-26455a0ddbd2"},{"name":"Contrasting field label color","score":10,"weight":8,"description":"Similar to having clear borders for your fields it's also of importance that the labels of your fields are easily readable. For this check we audit the text-color of your label compared to it's background. A good and clear field label displayed above a field contributes to your overall conversion rate.","id":"b9d1c2ad-a235-4584-94e4-e04a031404c8"},{"name":"Font size large enough for field labels","score":8.1,"weight":4,"description":"All modern browsers apply a good default font size, approximate 16px. It's advised not to override or set a default font size. When text needs to stand out or be smaller than the default, you can proportionally size it like 125% or 85%.","id":"5d48e23d-2a15-4332-a171-d41be828e7e5"},{"name":"Sufficient field label spacing","score":8.8,"weight":2,"description":"You should maintain sufficient spacing between your labels and your form fields. A guideline is at least 60% of your default font size.","id":"bb001eea-b9a9-47bf-805c-4f38108b2ddb"}]},"Usability":{"weight":6,"score":7.7,"description":"With form usability we focus on how easy it's for your website visitors to interact and complete your form on any device (Desktop computer, Mobile phone, tablet, etc).","link":"https:\/\/en.wikipedia.org\/wiki\/Web_usability","checks":[{"name":"Autocomplete enabled","score":10,"weight":9,"description":"Autocomplete assists your visitors with filling in your forms, this by using the latest HTML standards or manually setting the autocomplete attributes. Some browsers require the field to be part of a <code class=\"inline\">&lt;form&gt;<\/code>, the field needs to have a <code class=\"inline\">name<\/code> or <code class=\"inline\">id<\/code> attribute and the form needs to have a submit button.","id":"cae07140-d935-4aea-9279-558d0966c3d4"},{"name":"CTA button color","score":10,"weight":4,"description":"Your submit button or Call-To-Action should stand out and should be clearly visible. Most brands choose color their CTA buttons in their primary color. The <a href=\"https:\/\/webaim.org\/articles\/contrast\/\" target=\"_blank\">WCAG 2<\/a> standard defines a good set of contrast ratios.","id":"c41e595f-bd90-4cd0-ba44-654344f0b91a"},{"name":"CTA button text","score":10,"weight":4,"description":"Your submit button or Call-To-Action should be clear and hereby explains to your customer what will happen and\/or what is the next step. It's not advised to use the default values: Senden, Submit, Enviar, Envoyer, \u041d\u0430\u0434\u0456\u0441\u043b\u0430\u0442\u0438, Verzenden, and more. Having a good CTA increases conversions.","id":"98158341-3e68-4295-b464-375212ebf25a"},{"name":"Captcha","score":0,"weight":null,"description":"Often there is a need to address spam or bot entries on your form. Many techniques exist but one of the most popular ones is the usage of (re)captcha, which verifies through user-interaction if its a bot or not. Studies have showed that implementing this can have a negative effect on conversions, depending on the type of captcha you implement. Sometimes it's worth having a bit of unwanted entries, or filter them out on the server-side instead of having the user to complete a captcha.","id":"7de20532-c133-44e5-9a2a-bfdb90e906d9"},{"name":"Dropdown number of items","score":6.4,"weight":4,"description":"Dropdowns are best suited when the user needs to choose from 4 items or more. If there less than 4 items to choose form it's best to use (styled) radio buttons as they require less interaction and all options are immediately visible.","id":"02aa617c-8862-4d8c-9a98-96e443503b69"},{"name":"Field repetition","score":4,"weight":8,"description":"Asking your customers to enter multiple times the same data is hurting your conversion rates. This used to be a technique to verify if you entered your email, password or other data correctly. Today many tools like browser auto-fill, password and data managers exist to aid that process and make input faster, better and more secure. Our advice is to only ask for data once and build good processes to help your customer to verify, correct or recover data (confirm subscription by email, password recovery, login links or codes, etc.).","id":"a199e561-1fc5-43b4-b046-5082c3ce1ca5"},{"name":"First field is focussed","score":0,"weight":4,"description":"Autofocussing your first form field makes it easy for your visitors to start immediately. An additional benefit when you embrace accessibility with CSS using pseudo-classes like :focus could provide another visual indicator that the field is focused.","id":"9344830c-5c70-4dec-9613-13a1fe6b7879"},{"name":"Multiple phone number fields","score":10,"weight":4,"description":"Asking to repeat a phone number or request multiple types of phone numbers (Ex: mobile and fixed line) lowers your conversion rate. When a phone number is required, just ask one.","id":"ceea0f61-39f2-4f43-b8d7-e6e7b1325d0b"},{"name":"Natural keyboard navigation","score":10,"weight":7,"description":"Visitors should be able to tab through the elements in their natural position.","id":"b9c54263-8db8-4ce5-9130-05da7a99cfb5"},{"name":"Number of fields","score":6.8,"weight":9,"description":"While it's not an exact science and differs per industry vertical it's a good practice to reduce number of fields in your form to a minimum (approximate 3). Only ask the questions that are relevant for the goal of the form and try to prevent using textarea's as they usually drive down your conversion rate.","id":"0697219a-e8ca-4ea4-9b76-29d484516e9c"},{"name":"Radio button number of items","score":10,"weight":4,"description":"When your users needs to make a choice and the number of items to choose from are 4 or less it's a good fit. If your present more than 4 options, you probably want to limit the number of choices, provide an other option and a field for input or a dropdown with all available options.","id":"4b19cdd7-4db8-41e7-b41c-dde145373209"},{"name":"Reset button usage","score":10,"weight":null,"description":"Reset buttons were a thing in the nineties. Today reset buttons should not be used anymore as they're rarely useful, and are instead more likely to frustrate users who click them by mistake (often while trying to click the submit button) and thus clearing all their input.","id":"1d58bcde-30fa-4e95-bc2f-aeed4bcc0eca"},{"name":"Responsive keyboard","description":"If your visitors need to fill out a field in which only number are asked, it is best to automatically supply the user with a numeric keyboard. This only applies to mobile users. As soon as a field is given the characteristics of a numeric field, the mobile device should automatically supply the user with a numeric field. Pro-tip: did you know you can <a href=\"https:\/\/developer.mozilla.org\/en-US\/docs\/Web\/HTML\/Global_attributes\/enterkeyhint\" target=\"_blank\">customize<\/a> the action button on mobile keyboard to reflect the next step to: Done, Open, Next, Previous, Search or Send?","score":null,"weight":null,"id":"0a18a9b9-616d-4f52-8688-5ec80f19563e"},{"name":"Show progress","description":"Adding a progress bar, especially in larger or multi-step forms shows your users in which step of the conversion process they are. Showing a progress bar triggers the intrinsic need to finish something, in this case, the form.","score":null,"weight":null,"id":"57b5cd92-1613-4aaf-869b-4dacd2fbefa4"}]},"Validation":{"weight":4,"score":10,"description":"For visitors it should be clear what form fields are required and which ones are optional, and field validation should be provided as soon as possible, so the visitor can correct its input while the context still exists.","link":null,"checks":[{"name":"Submit button should be active","score":10,"weight":3,"description":"The submit button should not be disabled. Disabling the submit or continue button is deemed as bad practice as your Call To Action (CTA) will stand less out.","id":"2ce75cfb-4fc2-4c5d-a54d-e2095fa9fd95"}]},"Style":{"weight":3,"score":10,"description":"With today's modern web technologies styling your website and forms can be taken to the next level. Like layout and usability, it's vital that your forms are built consistently so that the user experience is maximised.","link":null,"checks":[{"name":"Floating labels","description":"It is important your form takes up as little space as possible. If you use <a href=\"https:\/\/getbootstrap.com\/docs\/5.2\/forms\/floating-labels\/\" target=\"_blank\">floating labels<\/a> combined with placeholders, the label is always visible to the user and does not leave the field. Because the label is integrated in the field it takes up less space and the fields can be located closer together.","score":null,"weight":null,"id":"6117d48f-9891-4f44-8945-1032e61bdd08"},{"name":"Form branding","description":"To make sure your visitors trust your form, you must make sure they can recognize your brand during the filling out of the form. Many forms are just black and white because that supposedly would have been the best colors for a form. However, it is important to let your visitors know that they are still on your website. You can also improve the user experience by adding your logo to the page of your forms and adding your USP\u2019s (Unique Selling Proposition).","score":null,"weight":null,"id":"53ea942d-b5f0-4f33-8179-93fc51c32a41"},{"name":"Form font consistency","score":10,"weight":5,"description":"It's advised not to use more than two or more different fonts within your form.","id":"9e256c27-8d40-40df-a700-01bee9d533be"},{"name":"Grouping of fields","description":"If your form includes about 6 or more fields, it is important to group the questions you are asking. A visitor can be comforted by looking at two separated segments instead of one big list of questions.","score":null,"weight":null,"id":"6dee900c-3fb3-4bc6-8d8f-d47b99564525"},{"name":"Responsive design","description":"To have the best user experience on your online forms for all devices and browsers, it is important to have a responsive design. This means that the form must adapt to the device it is being displayed on. For example: if the form is displayed on a mobile device, the buttons should be larger than the buttons on the desktop version because visitors use their fingers on their screens to activate them.","score":null,"weight":null,"id":"dd4e2446-2418-4c4f-955f-2f3911dd94fc"}]},"Web Standards":{"weight":6,"score":4.1,"description":"Websites and forms are built with a mix of HTML, Stylesheets and JavaScript. Luckily there are standards and best practices for each. Using those will result in a better User Experience and provide cross-device compatibility.","link":"https:\/\/en.wikipedia.org\/wiki\/Web_standards","checks":[{"name":"Email field type","score":0,"weight":8,"description":"Often a regular text field is used for email address entry. All browsers support the <code class=\"inline\">&lt;input type=&quot;email&quot; \/&gt;<\/code> email field type that automatically do basic email address validation, implement autocomplete functionality and provides the correct keyboard layout on mobile devices.","id":"e26e0bce-6cae-4a52-8a66-c30ebe8608f0"},{"name":"Field labels","score":7.3,"weight":7,"description":"Each form field should have a corresponding label","id":"a6de9151-1771-4263-8207-9e55ee7961bc"},{"name":"Form element","score":9.3,"weight":2,"description":"Without a <code class=\"inline\">&lt;form&gt;<\/code> tag, the autocomplete functionality will not always work as expected. Visit our <a href=\"https:\/\/support.exatom.io\/portal\/en\/kb\/articles\/adding-a-form-element\" target=\"_blank\">knowledge base<\/a> on how to add a <code class=\"inline\">&lt;form&gt;<\/code> tag around your fields and ensure autocomplete is working.","id":"8d176787-3c42-4a8c-ba7d-d5212c16508b"}]},"Performance":{"weight":5,"score":3.6,"description":"Web performance refers to how quickly site content loads and renders in a web browser and how well it responds to user interaction. Bad performing sites are slow to display and slow to respond to input. Bad performing sites increase site abandonment. At its worst, bad performance causes content to be completely inaccessible.","link":"https:\/\/developer.mozilla.org\/en-US\/docs\/Learn\/Performance\/why_web_performance","checks":[{"name":"Page speed","score":3.6,"weight":8,"description":"It's vital that your website and certainly pages with forms for conversion load fast. Generally visitors expect to be able to interact with a page in less than 2 seconds.","id":"a735aaa1-7377-48d5-9985-3c7e6121461a"}]},"Layout":{"weight":6,"score":5.4,"description":"A well thought out web page and form layout is what contributes to the overall success of your website, and ultimately the conversion-rate of your forms.","link":null,"checks":[{"name":"Field alignment and size","score":5.1,"weight":8,"description":"Form fields should be aligned as much as possible and their size should be consistent.","id":"3de3f67c-a232-45dc-aefb-0c650280690d"},{"name":"Form position","score":2.5,"weight":7,"description":"Forms that are immediately in-view for your website visitor convert best. Our scoring algorithm takes the most prominent form and determines it's position. ","id":"3e4de45a-113e-426d-a76c-cc8657dcc992"},{"name":"Form size","score":7,"weight":5,"description":"Ideally your form is as concise as possible and should fit on a desktop computers screen.","id":"857ca147-b333-4574-8030-3088e6268f70"},{"name":"Labels above fields","score":10,"weight":3,"description":"Multiple studies and benchmarks agree on that labels above input fields are the best position. Certainly on smaller screen devices this has an advantage as the input field can be larger.","id":"f1e47bbc-fe50-4b46-94f1-e7273ebc9fe6"}]},"Privacy":{"weight":2,"score":10,"description":"In today's digital world, privacy should not be taken with a grain of salt and should be, like Exatom, be one of the basic principles for building or evolving your business.","link":"https:\/\/exatom.io\/data-journey","checks":[{"name":"Password requirements","description":"Every organization or website has certain password requirements, there is no standard list of requirements. It\u2019s hereby of importance to provide the requirements next to your password field as microcopy. So, users know what\u2019s needed to continue and ideally each set of your requirements color green once met.","score":null,"weight":null,"id":"74128328-3773-42e0-804f-4f93568a20b0"},{"name":"Repeated password fields","description":"Forcing your visitors to create an account is always a no-go. If you have no other choice but to do so, let your visitors do this with as few required fields as possible. Don\u2019t make your visitors repeat their chosen password, certainly not in the case when you offer a \u2018forgot your password\u2019 function. Additionally giving your visitors the ability to view the password for verification will help them converting. This is <a href=\"https:\/\/css-tricks.com\/the-options-for-password-revealing-inputs\/\" target=\"_blank\">usually done with a button<\/a> with the symbol of an eye. ","score":null,"weight":null,"id":"e4736546-cf27-40b6-8b78-3c8718c73407"},{"name":"Secure password fields","score":10,"weight":8,"description":"All password entries should be masked by default and should use the browser field attribute <code class=\"inline\">type=\"password\"<\/code>. Any other type by default would allow others to physically see and programmatically access the entered password.","id":"1f5556e2-20e9-4bb4-ba67-8ca02970f5c0"},{"name":"Sensitive information microcopy","description":"If your visitors need to fill out sensitive information, it is always wise to tell them why you are asking that information. For example: if you ask your visitors for their phone number, you should tell them that the number will only be used in case of a problem with the delivery.","score":null,"weight":null,"id":"87ab805d-a591-43ba-8f89-d043b0d5be48"}]}},"userHash":"6411ba19-c463-4506-b84c-7005e8859af4","uri":"https:\/\/www.inno.be\/nl\/my-account\/create","ts":"2022-11-30 15:22:16.275","ui":"http:\/\/localhost:8081\/result\/bffc87fe-a5e2-4db4-add6-34eaa68ad56d","json":"https:\/\/exassets.s3.eu-central-1.amazonaws.com\/scores\/bffc87fe-a5e2-4db4-add6-34eaa68ad56d.json"}
        // this.uri = this.scores.uri
      }

      this.loading = false
    },
    restart () {
      this.scores = {}
      this.uri = ''
      if (!this.isUi) {
        this.$router.push('/')
      }
    },
    urlFull (uri) {
      uri ??= this.uri
      if (!uri.match(/^https?:\/\//gi)) {
        uri = 'https://' + uri
      }
      return uri
    },
    scoreUri () {
      // Validation
      this.couldNotScore = false
      this.disallowedDomain = false

      this.$refs.uriInput.$el.classList.remove('is-invalid')
      if (!this.uri || !this.uri.length) {
        this.$refs.uriInput.$el.classList.add('is-invalid')
        return
      }

      if (this.isUi && !this.$root.hasRole('admin.internal') && !this.$root.isValidDomain(this.uri)) {
        this.disallowedDomain = true
        return
      }

      this.getScore(this.uri)
    },
    async getScore (uri) {
      uri = this.urlFull(uri)

      this.loading = true
      const scoring = (await this.$api.assets('/score', { uri: uri }).catch(() => {
      }))?.data

      this.loading = false

      if (scoring && Object.values(scoring).length) {
        this.scores = {
          ...scoring,
          uri: uri
        }
        this.$root.$api.addTags('exatomConversion')

        if (!this.isUi && this.scores?.ui) {
          history.replaceState({}, '', document.location.origin + '/result/' + this.scores?.ui?.split('/').pop())
        }
      } else {
        this.couldNotScore = true
      }
    },
    toggleShowImprovementAreasOnly () {
      this.showImprovementAreasOnly = !this.showImprovementAreasOnly

      const uri = new URL(document.location.href)
      if (this.showImprovementAreasOnly) {
        uri.searchParams.append('showImprovementAreasOnly', 1)
      } else {
        uri.searchParams.delete('showImprovementAreasOnly')
      }
      history.replaceState({}, '', uri.toString())

      setTimeout(() => {
        for (const group of Object.values(this.scores.details)) {
          for (const check of group.checks) {
            this.$refs[check.id][0].show = this.showImprovementAreasOnly
          }
        }
      }, 100)
    },
    async sendAuditReport () {
      this.formAuditFullReport.nameError = false
      this.formAuditFullReport.emailError = false
      this.formAuditFullReport.reportSuccess = null

      if (!this.$refs.yourName.$el.validity.valid) {
        this.formAuditFullReport.nameError = true
      }
      if (!this.$refs.yourEmail.$el.validity.valid) {
        this.formAuditFullReport.emailError = true
      }
      if (this.formAuditFullReport.nameError || this.formAuditFullReport.emailError) {
        return false
      }

      // Send actual report
      const report = (await this.$api.assets('/score/receive-full-report', {
        name: this.formAuditFullReport.name,
        email: this.formAuditFullReport.email,
        auditUri: this.uri,
        scoreResultUri: document.location.href,
        auditJsonUri: this.scoreData.json,
        score: this.scoreData.score,
        lang: document.documentElement.getAttribute('lang') ?? 'en-EN'
      }).catch(e => {
        if (e.response?.data?.error?.indexOf('email') !== -1) {
          // No work email entered
          this.formAuditFullReport.emailError = true
          return
        }
        this.formAuditFullReport.reportSuccess = false
      }))
      if (report && report.status !== 202) {
        this.formAuditFullReport.reportSuccess = false
        return false
      } else if (report && report.status === 202) {
        this.formAuditFullReport.reportSuccess = true
        return true
      }
    },
    async downloadAsCsv () {
      if (!this.scoreData) return
      const cats = []
      const checks = []
      const scores = []
      const result = []

      for (const cat of ['Accessibility', 'Performance', 'Privacy', 'Style', 'Usability', 'Validation', 'Web Standards']) {
        for (const check of this.scoreData.details[cat].checks) {
          cats.push(cat)
          checks.push(check.name)
          scores.push(check.score)
        }
      }

      // Header
      result.push(this.shareUri)
      result.push('')
      result.push(cats.join(','))
      result.push(checks.join(','))
      result.push(scores.join(','))

      await this.download(result.join('\r\n'), `Exatom Form Audit Score - ${this.resultHash}.csv`)
    },
    async download (data, filename) {
      const blob = new Blob([data], {
        type: 'text/csv;charset=utf-8'
      })
      const a = document.createElement('a')
      a.href = window.URL.createObjectURL(blob)
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
    }
  },
  watch: {
    '$root.client': {
      async handler () {
        await this.reload()
      },
      immediate: true
    },
    'this.$route.query': {
      async handler () {
        if (Object.keys(this.$route.query).includes('showImprovementAreasOnly')) {
          this.showImprovementAreasOnly = true
        }
      },
      immediate: true
    }
  }
}
</script>

<style scoped>
@import '~animate.css';

input::placeholder { /* Chrome, Firefox, Opera, Safari 10.1+ */
  color: var(--exatom-grey-plus2) !important;
  opacity: 1; /* Firefox */
}

input:user-invalid {
  border: 1px solid var(--exatom-red);
}

input:user-valid {
  border: 1px solid var(--exatom-green-plus1);
}
</style>
<style>
#scoreApp {
  max-width: 60rem;
  margin-left: auto;
  margin-right: auto;
  background-color: #F6F6F6;
}

footer a, footer a:hover {
  color: #888;
}

footer .social a {
  font-size: 1rem;
  color: var(--exatom-grey-plus1);
}
footer .social a:hover {
  color: var(--exatom-grey-plus2);
}

.bg-scoreAccessibility {
  background-color: var(--teal) !important;
}

.bg-scoreUsability {
  background-color: var(--indigo) !important;
}

.bg-scoreValidation {
  background-color: var(--exatom-green) !important;
}

.bg-scoreStyle {
  background-color: var(--exatom-yellow) !important;
}

.bg-scoreWebStandards {
  background-color: var(--purple) !important;
}

.bg-scorePerformance {
  background-color: var(--exatom-red) !important;
}

.bg-scoreLayout {
  background-color: var(--exatom-grey-plus2) !important;
}

.bg-scoreSecurity {
  background-color: var(--pink) !important;
}

.bg-scorePrivacy {
  background-color: var(--exatom-blue-min1) !important;
}

.bg-scoreE-Commerce {
  background-color: var(--exatom-green) !important;
}

:root {
  --color-scoreAccessibility: var(--teal) !important;
  --color-scoreUsability: var(--indigo) !important;
  --color-scoreValidation: var(--exatom-green) !important;
  --color-scoreStyle: var(--exatom-yellow) !important;
  --color-scoreWebStandards: var(--purple) !important;
  --color-scorePerformance: var(--exatom-red) !important;
  --color-scoreLayout: var(--exatom-grey-plus2) !important;
  --color-scoreSecurity: var(--pink) !important;
  --color-scorePrivacy: var(--exatom-blue-min1) !important;
  --color-scoreE-Commerce: var(--exatom-green) !important;
}

.progress {
  background: transparent !important;
}

.progress-bar {
  border-radius: 10rem !important;
}

.breakout-group-score > .b-avatar-text {
  font-size: 1.4rem !important;
  letter-spacing: unset;
}

.scoreGroupDescriptionTextOutline {
  shape-outside: circle(40%);
  width: 6rem;
  height: 4rem;
  float: right;
  margin-top: -3rem;
}
.no-underline:hover {
  text-decoration: none !important;
}

@media print {
  .b-avatar-text {
    mask-image: none !important;
  }
}

</style>
